import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useGlobalContext } from "../../context/globalContext";
import { useStepsContext } from "../../context/PIstepsContext";
import useServiceType from "../../hooks/useServiseType";
import Loader from "../../shared/loaders/simpleLoader";
import Steps from "../../shared/steps/steps";
import { SERVICE_TYPES } from "../../utils/constants/fieldErrors";
import { FormProvider, useForm } from "react-hook-form";
import {
  PERSONAL_INFO_STEPS,
  YES_NO_VAL,
} from "../../components/PIForm/Steps/step3/utils";
import {
  getClientTypeIdFromOnboardingToken,
  redirectToPiOnboardingIdentification,
} from "../../components/piOnboarding/steps/helpers";
import { isPageReload } from "../../utils/helpers/isPageReload";
import {
  getPersistedIdentificationStatus,
  PI_FORM_STEP_ROUTES,
} from "../../utils/constants/piFormSession";

const Step2 = lazy(() => import("../../components/PIForm/Steps/step2/step2"));
const PersonalDetails = lazy(() =>
  import("../../components/PIForm/Steps/step3")
);
const Step5 = lazy(() => import("../../components/PIForm/Steps/step5"));

export function PIStepRoutes() {
  const { steps, activeStep, setActiveStep } = useStepsContext();
  const [step, setStep] = useState(PERSONAL_INFO_STEPS.personDetails);

  const { i18n } = useTranslation();
  const location = useLocation();
  const {
    globalData: { status, person },
  } = useGlobalContext();
  const { serviceType } = useServiceType();
  const clientTypeId = getClientTypeIdFromOnboardingToken();

  const effectiveStatus = status || getPersistedIdentificationStatus();
  const needsIdentification = useMemo(
    () => effectiveStatus !== "Approved",
    [effectiveStatus]
  );

  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      dualCitizenShip: YES_NO_VAL.no.toString(),
      isGreenCardHolder: YES_NO_VAL.no.toString(),
      isPep: YES_NO_VAL.no.toString(),
      tax_residency_question: YES_NO_VAL.no.toString(),
      legalAddress: person?.juridicalAddress ?? "",
      tax_residency_countries: [{ countryId: "", taxCode: "" }],
    },
  });

  if (isPageReload() && PI_FORM_STEP_ROUTES.includes(location.pathname)) {
    redirectToPiOnboardingIdentification();
    return (
      <Steps
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        hideSteps={false}
      >
        <Loader />
      </Steps>
    );
  }

  return (
    <Steps
      steps={steps}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      hideSteps={serviceType !== SERVICE_TYPES.PIForm && true}
    >
      <Suspense fallback={<Loader />}>
        {needsIdentification ? (
          <Switch>
            <Route component={Step2} path={`/Identomat/${i18n.language}`} />
            <Redirect to={`/Identomat/${i18n.language}`} />
          </Switch>
        ) : (
          <FormProvider {...methods}>
            <Switch>
              <Route
                component={() => (
                  <PersonalDetails
                    person={person}
                    clientTypeId={clientTypeId}
                    step={step}
                    setStep={setStep}
                  />
                )}
                path={`/step/3`}
              />

              <Route
                component={() => <Step5 clientTypeId={clientTypeId} />}
                path={`/step/4`}
              />

              <Redirect to={`/step/3`} />
            </Switch>
          </FormProvider>
        )}
      </Suspense>
    </Steps>
  );
}
